// Auto-fill helper (web): maps the surveyor's captured GPS position onto the
// survey's location questions. Address questions store a JSON object
// { formatted, lat, lng }; latitude/longitude/GPS text questions store a
// string. Filled fields are rendered read-only in SurveyForm.
import type { SchemaShape, Question } from '@/components/SurveyForm';

type GeoKind = 'lat' | 'lng' | 'coord' | 'address' | null;

const INPUT_TYPES = ['short_text', 'long_text', 'number', 'currency', 'email', 'phone', 'date', 'slider'];

function geoKind(q: Question): GeoKind {
  if (q.type === 'address' || q.type === 'address_only' || q.type === 'geo_only') return 'address';
  const t = (q.text || '').toLowerCase();
  const hasLat = /latitude/.test(t) || /\blat\b/.test(t);
  const hasLng = /longitude/.test(t) || /\blng\b/.test(t) || /\blon\b/.test(t) || /\blong\b/.test(t);
  const hasGps = /\bgps\b/.test(t) || /co-?ordinate/.test(t);
  if (!INPUT_TYPES.includes(q.type)) return null;
  if (hasLat && hasLng) return 'coord';
  if (hasGps) return 'coord';
  if (hasLat) return 'lat';
  if (hasLng) return 'lng';
  return null;
}

export function computeGeoAutofill(
  schema: SchemaShape,
  lat: number,
  lng: number,
): { values: Record<string, string>; locked: string[] } {
  const latS = String(lat);
  const lngS = String(lng);
  const values: Record<string, string> = {};
  const locked: string[] = [];
  for (const sec of schema.sections) {
    for (const q of sec.questions) {
      const kind = geoKind(q);
      if (!kind) continue;
      values[q.question_code] =
        kind === 'address'
          ? JSON.stringify({ lat, lng })
          : kind === 'lat'
            ? latS
            : kind === 'lng'
              ? lngS
              : `${latS}, ${lngS}`;
      // Address fields stay editable (surveyor picks/confirms the physical
      // address); pure coordinate fields are locked read-only.
      if (kind !== 'address') locked.push(q.question_code);
    }
  }
  return { values, locked };
}
